import React, { useState, useRef, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ExerciseDemoButton } from '../ExerciseDemoButton';

interface ExerciseCardProps {
  name: string;
  sets: number;
  reps: number;
  index: number;
  updateExercise: (index: number, field: string, value: any) => void;
  removeExercise: (index: number) => void;
}

export function ExerciseCard({ name, sets, reps, index, updateExercise, removeExercise }: ExerciseCardProps) {
  const { theme } = useTheme();
  const [editingField, setEditingField] = useState<'sets' | 'reps' | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingField]);

  const commitValue = (field: 'sets' | 'reps', raw: string) => {
    const parsed = parseInt(raw);
    if (!isNaN(parsed) && parsed >= 0) {
      updateExercise(index, field, parsed);
    }
    setEditingField(null);
  };

  return (
    <div className={`px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card}`}>
      <div className='flex items-center justify-between gap-2 mb-1.5'>
        <span className='font-semibold text-sm truncate flex-1'>{name || 'Sin nombre'}</span>
        <ExerciseDemoButton name={name} iconClassName='w-4 h-4' className='p-1.5' />
        <button
          type='button'
          onClick={() => removeExercise(index)}
          className='p-1.5 rounded-lg hover:bg-red-600 active:bg-red-700 text-red-500 hover:text-white touch-manipulation flex-shrink-0'
          aria-label='Eliminar ejercicio'
        >
          <Trash2 className='w-4 h-4' />
        </button>
      </div>
      <div className='flex gap-2'>
        {editingField === 'sets' ? (
          <input
            ref={inputRef}
            type='number'
            defaultValue={sets}
            min={0}
            onBlur={(e) => commitValue('sets', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitValue('sets', (e.target as HTMLInputElement).value);
            }}
            className={`w-20 px-2.5 py-1 rounded-lg border ${theme.input} text-xs text-center`}
          />
        ) : (
          <button
            type='button'
            onClick={() => setEditingField('sets')}
            className={`px-2.5 py-1 rounded-lg border ${theme.border} text-xs font-medium ${theme.hover} touch-manipulation`}
          >
            {sets} series
          </button>
        )}

        {editingField === 'reps' ? (
          <input
            ref={inputRef}
            type='number'
            defaultValue={reps}
            min={0}
            onBlur={(e) => commitValue('reps', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitValue('reps', (e.target as HTMLInputElement).value);
            }}
            className={`w-20 px-2.5 py-1 rounded-lg border ${theme.input} text-xs text-center`}
          />
        ) : (
          <button
            type='button'
            onClick={() => setEditingField('reps')}
            className={`px-2.5 py-1 rounded-lg border ${theme.border} text-xs font-medium ${theme.hover} touch-manipulation`}
          >
            {reps} reps
          </button>
        )}
      </div>
    </div>
  );
}
